// M-152: 販促 KPI — 配信への時間窓帰属（ラストタッチ）と日次 / 月次集計の**純関数**。
//
// ## 決定事項（M-150 / ユーザー決定 2026-09-27）
//
// - 帰属は **配信後 N 日窓のラストタッチ**（既定 7 日 / 商材ごとに上書き可）。同一顧客に複数配信が
//   あれば **最後に受けた配信**に付ける（マルチタッチ按分は不採用 = 担当者が説明できることを優先）
// - **明示帰属 > 時間窓帰属**。`campaignId` を持ち回った行動（リンクタップ経由の予約完了 /
//   クーポン取得 等）はその配信に付け、時間窓は見ない（二重計上しない）
// - 全商材・全 KPI を同一ロジックで計算する。商材ごとの「見せる KPI の選択」は表示側（M-153）
// - 帰属できない行動は捨てず **「配信外（organic）」**として数える（配信効果の分母比較のため）
//
// ## 入力の正規化（functions 側 loader の責務）
//
// - 顧客キーは `line:<lineUserId>`（promo_events.userId と同形）。`line_events` は lineUserId から、
//   bookings は `customerUserId ?? userId` から作る。LINE 未連携の予約（admin uid）は顧客キー null
//   → 帰属不能として organic に数える
// - 時刻はすべて JST の Iso8601 文字列（`YYYY-MM-DDTHH:mm:ss+09:00`）。窓の比較は epoch ms
// - 「配信」= `line_events.message_sent` を `resolveOutboundDeliveryKey` で配信キーに直したもの。
//   予約系通知（booking_*）・手動チャット・webhook 返信は配信ではない（`PROMO_DELIVERY_ORIGINS`）
// - テスト配信（`promo_campaigns.isTest`）は sent にも帰属先にも数えない
//
// ## 構造的な限界（doc に `attributionMode` で残す）
//
// - `message_sent` の記録は 2026-09-26（M-148）から。それ以前の配信は宛先が無く、明示帰属だけになる
// - 全員配信（broadcast）は宛先を持たない（M-151）。同じく明示帰属のみ
import { PROMO_INTEREST_KEYS } from './types.js';
import { isPromoDeliveryOrigin, } from './lineOutboundDelivery.js';
export const PROMO_KPI_SCHEMA_VERSION = 1;
/**
 * 宛先スナップショット（`line_events.message_sent`）の記録開始日（M-148）。これより前の配信は
 * 「誰に届いたか」が無く、配信履歴の効果は明示帰属（リンク経由）だけになる = 部分帰属。
 */
export const PROMO_KPI_RECIPIENT_SNAPSHOT_SINCE = '2026-09-26';
export const PROMO_KPI_DEFAULT_WINDOW_DAYS = 7;
/** 商材別上書きの上限（車検など長期商材向け / これ以上は「帰属」と呼べない）。 */
export const PROMO_KPI_MAX_WINDOW_DAYS = 90;
export function emptyCounters() {
    return {
        taps: 0,
        surveyAnswers: 0,
        optOuts: 0,
        bookings: 0,
        couponClaims: 0,
        couponRedeems: 0,
        unfollows: 0,
        stageUps: 0,
    };
}
const COUNTER_BY_TYPE = {
    tap: 'taps',
    survey_answer: 'surveyAnswers',
    opt_out: 'optOuts',
    booking: 'bookings',
    coupon_claim: 'couponClaims',
    coupon_redeem: 'couponRedeems',
    unfollow: 'unfollows',
    stage_up: 'stageUps',
};
/** 配信キー → 安定 id（販促の配信でなければ null）。 */
export function promoDeliveryId(key) {
    switch (key.kind) {
        case 'campaign':
            return `campaign:${key.campaignId}`;
        case 'scenario':
            return `scenario:${key.scenarioId}`;
        case 'scheduled_message':
            return `scheduled_message:${key.templateId ?? key.scheduledMessageId}`;
        case 'coupon':
            return `coupon:${key.couponId}`;
        case 'diagnosis_thank_you':
            return `diagnosis_thank_you:${key.cycleId}`;
        default:
            return null;
    }
}
/** 商材別の窓日数（上書き > 既定 / 範囲外は既定に落とす）。 */
export function resolveWindowDays(interestKey, windowDaysDefault, byKey) {
    const clamp = (n) => Number.isInteger(n) && n >= 1 && n <= PROMO_KPI_MAX_WINDOW_DAYS ? n : null;
    const override = interestKey ? byKey?.[interestKey] : undefined;
    if (typeof override === 'number') {
        const c = clamp(override);
        if (c !== null)
            return c;
    }
    return clamp(windowDaysDefault) ?? PROMO_KPI_DEFAULT_WINDOW_DAYS;
}
const ms = (iso) => new Date(iso).getTime();
const DAY_MS = 86_400_000;
function newDeliveryStats(row, meta) {
    return {
        ...emptyCounters(),
        id: row.id,
        kind: row.key.kind,
        interestKey: meta?.interestKey ?? null,
        ...(meta?.label ? { label: meta.label } : {}),
        sentMessages: 0,
        sentUsers: 0,
        explicit: 0,
        windowed: 0,
        windowUsers: 0,
        stageUpsByTransition: {},
    };
}
/**
 * 1 日ぶんの KPI を計算する（純関数 / 決定的）。
 *
 * - `sends` は窓内すべて（当日を含む）。当日の送信は `sentMessages/sentUsers` に、窓内全体は帰属候補に使う
 * - `events` は当日分だけ。各行動は explicit > windowed の順で配信に付け、付かなければ organic
 */
export function computeDailyKpi(input) {
    const dayStart = ms(`${input.date}T00:00:00+09:00`);
    const dayEnd = dayStart + DAY_MS;
    // 販促の配信だけを行に直す（テスト配信は落とす）
    const rows = [];
    for (const s of input.sends) {
        if (!isPromoDeliveryOrigin(s.key.origin))
            continue;
        const id = promoDeliveryId(s.key);
        if (!id)
            continue;
        if (input.deliveryMeta[id]?.isTest)
            continue;
        rows.push({ customer: s.customer, sentAtMs: ms(s.sentAt), sentAt: s.sentAt, id, key: s.key });
    }
    rows.sort((a, b) => a.sentAtMs - b.sentAtMs);
    const deliveries = new Map();
    const ensure = (id, key) => {
        let d = deliveries.get(id);
        if (!d) {
            d = newDeliveryStats({ id, key }, input.deliveryMeta[id]);
            deliveries.set(id, d);
        }
        return d;
    };
    // 当日の到達
    const sentUsersById = new Map();
    const windowUsersById = new Map();
    const byCustomer = new Map();
    for (const r of rows) {
        const d = ensure(r.id, r.key);
        (windowUsersById.get(r.id) ?? windowUsersById.set(r.id, new Set()).get(r.id)).add(r.customer);
        if (r.sentAtMs >= dayStart && r.sentAtMs < dayEnd) {
            d.sentMessages += 1;
            (sentUsersById.get(r.id) ?? sentUsersById.set(r.id, new Set()).get(r.id)).add(r.customer);
        }
        (byCustomer.get(r.customer) ?? byCustomer.set(r.customer, []).get(r.customer)).push(r);
    }
    for (const [id, users] of sentUsersById)
        deliveries.get(id).sentUsers = users.size;
    for (const [id, users] of windowUsersById)
        deliveries.get(id).windowUsers = users.size;
    const byInterestKey = {};
    const bucket = (k) => {
        const name = k ?? 'none';
        return (byInterestKey[name] ??= {
            sentMessages: 0,
            attributed: emptyCounters(),
            organic: emptyCounters(),
        });
    };
    for (const d of deliveries.values())
        bucket(d.interestKey).sentMessages += d.sentMessages;
    const organic = emptyCounters();
    const totals = { ...emptyCounters(), sentMessages: 0, attributed: 0 };
    const lastTouch = (e) => {
        if (!e.customer)
            return null;
        const list = byCustomer.get(e.customer);
        if (!list)
            return null;
        const at = ms(e.at);
        let best = null;
        for (const r of list) {
            if (r.sentAtMs > at)
                break;
            const meta = input.deliveryMeta[r.id];
            const days = resolveWindowDays(meta?.interestKey ?? e.interestKey, input.windowDaysDefault, input.windowDaysByInterestKey);
            if (at - r.sentAtMs <= days * DAY_MS)
                best = r; // 昇順なので最後に残ったものがラストタッチ
        }
        return best;
    };
    for (const e of input.events) {
        const counter = COUNTER_BY_TYPE[e.type];
        totals[counter] += 1;
        let target = null;
        let mode = null;
        // 明示 campaignId は **自テナントの promo_campaigns に実在する配信**（= loader が deliveryMeta を
        // 付けたもの）だけを採用する。campaignId は顧客 LIFF が持ち回る値で詐称できるため、未知の id を
        // そのまま配信行にすると (a) 任意の配信に成果を付け替えられ (b) 一意な文字列を量産して
        // deliveries[] を膨らませ doc の 1MiB 上限で set を落とせる（PR #889 レビュー HIGH）。
        // 実在しない / テスト配信の明示 id は無視して時間窓へ倒す（タップは organic）。
        const explicitMeta = e.explicitCampaignId
            ? input.deliveryMeta[`campaign:${e.explicitCampaignId}`]
            : undefined;
        if (e.explicitCampaignId && explicitMeta && !explicitMeta.isTest) {
            target = ensure(`campaign:${e.explicitCampaignId}`, {
                kind: 'campaign',
                origin: 'segment_push',
                campaignId: e.explicitCampaignId,
            });
            mode = 'explicit';
        }
        else if (e.type !== 'tap') {
            // tap は campaignId 無しでは配信の行動と言えない（リッチメニュー等）→ organic
            const row = lastTouch(e);
            if (row) {
                target = deliveries.get(row.id);
                mode = 'windowed';
            }
        }
        if (target && mode) {
            target[counter] += 1;
            target[mode] += 1;
            if (e.type === 'stage_up' && e.transition)
                target.stageUpsByTransition[e.transition] =
                    (target.stageUpsByTransition[e.transition] ?? 0) + 1;
            bucket(target.interestKey ?? e.interestKey).attributed[counter] += 1;
            totals.attributed += 1;
        }
        else {
            organic[counter] += 1;
            bucket(e.interestKey).organic[counter] += 1;
        }
    }
    for (const d of deliveries.values())
        totals.sentMessages += d.sentMessages;
    return {
        version: PROMO_KPI_SCHEMA_VERSION,
        tenantId: input.tenantId,
        date: input.date,
        computedAt: input.computedAt,
        windowDaysDefault: input.windowDaysDefault,
        windowDaysByInterestKey: { ...(input.windowDaysByInterestKey ?? {}) },
        attributionMode: rows.length > 0 ? 'recipient' : 'link-only',
        deliveries: [...deliveries.values()].sort((a, b) => a.id.localeCompare(b.id)),
        byInterestKey,
        organic,
        totals,
    };
}
/**
 * doc の 1MiB 上限を守る防衛線: 配信行が上限を超えたら、到達 + 帰属の小さい順に `other` 行へ畳む。
 * 正常運用では 1 日の配信種類は数十件で、ここに到達するのは異常入力か大量配信のときだけ。
 */
export const PROMO_KPI_MAX_DELIVERIES = 200;
export const PROMO_KPI_OTHER_DELIVERY_ID = 'other';
export function capDeliveries(rows) {
    if (rows.length <= PROMO_KPI_MAX_DELIVERIES)
        return rows;
    const weight = (d) => d.sentMessages + d.explicit + d.windowed;
    const ranked = [...rows].sort((a, b) => weight(b) - weight(a) || a.id.localeCompare(b.id));
    const keep = ranked.slice(0, PROMO_KPI_MAX_DELIVERIES - 1);
    const other = {
        ...emptyCounters(),
        id: PROMO_KPI_OTHER_DELIVERY_ID,
        kind: 'campaign',
        interestKey: null,
        label: `その他 ${rows.length - keep.length} 配信`,
        sentMessages: 0,
        sentUsers: 0,
        explicit: 0,
        windowed: 0,
        windowUsers: 0,
        stageUpsByTransition: {},
    };
    for (const d of ranked.slice(keep.length)) {
        addCounters(other, d);
        other.sentMessages += d.sentMessages;
        other.sentUsers += d.sentUsers;
        other.windowUsers += d.windowUsers;
        other.explicit += d.explicit;
        other.windowed += d.windowed;
        for (const [t, n] of Object.entries(d.stageUpsByTransition))
            other.stageUpsByTransition[t] = (other.stageUpsByTransition[t] ?? 0) + n;
    }
    return [...keep, other].sort((a, b) => a.id.localeCompare(b.id));
}
const COUNTER_KEYS = Object.keys(emptyCounters());
/** カウンタ 8 キーだけを足す（配信 stats 全体を渡しても id 等の文字列を壊さない）。 */
function addCounters(into, from) {
    for (const k of COUNTER_KEYS)
        into[k] += from[k];
}
/** 月次 = 日次 doc の合算（純関数）。`sentUsers` / `windowUsers` は日次の合計（同一人物の重複あり）。 */
export function mergeMonthlyKpi(tenantId, month, dailies, computedAt) {
    const deliveries = new Map();
    const byInterestKey = {};
    const organic = emptyCounters();
    const totals = { ...emptyCounters(), sentMessages: 0, attributed: 0 };
    const modes = new Set();
    const days = [];
    for (const d of [...dailies].sort((a, b) => a.date.localeCompare(b.date))) {
        days.push(d.date);
        modes.add(d.attributionMode);
        for (const s of d.deliveries) {
            const cur = deliveries.get(s.id);
            if (!cur) {
                deliveries.set(s.id, { ...s, stageUpsByTransition: { ...s.stageUpsByTransition } });
                continue;
            }
            addCounters(cur, s);
            cur.sentMessages += s.sentMessages;
            cur.sentUsers += s.sentUsers;
            cur.windowUsers += s.windowUsers;
            cur.explicit += s.explicit;
            cur.windowed += s.windowed;
            for (const [t, n] of Object.entries(s.stageUpsByTransition))
                cur.stageUpsByTransition[t] = (cur.stageUpsByTransition[t] ?? 0) + n;
            if (!cur.label && s.label)
                cur.label = s.label;
            if (cur.interestKey === null && s.interestKey)
                cur.interestKey = s.interestKey;
        }
        for (const [k, b] of Object.entries(d.byInterestKey)) {
            const cur = (byInterestKey[k] ??= {
                sentMessages: 0,
                attributed: emptyCounters(),
                organic: emptyCounters(),
            });
            cur.sentMessages += b.sentMessages;
            addCounters(cur.attributed, b.attributed);
            addCounters(cur.organic, b.organic);
        }
        addCounters(organic, d.organic);
        addCounters(totals, d.totals);
        totals.sentMessages += d.totals.sentMessages;
        totals.attributed += d.totals.attributed;
    }
    return {
        version: PROMO_KPI_SCHEMA_VERSION,
        tenantId,
        month,
        computedAt,
        days,
        attributionMode: modes.size === 1 ? [...modes][0] : modes.size === 0 ? 'link-only' : 'mixed',
        deliveries: capDeliveries([...deliveries.values()].sort((a, b) => a.id.localeCompare(b.id))),
        byInterestKey,
        organic,
        totals,
    };
}
/** 集計 doc の id（`{tenantId}__{date}` / `{tenantId}__{YYYY-MM}`）。 */
export const promoKpiDailyDocId = (tenantId, date) => `${tenantId}__${date}`;
export const promoKpiMonthlyDocId = (tenantId, month) => `${tenantId}__${month}`;
/** 全商材の表示順（byInterestKey の 'none' は末尾）。 */
export const PROMO_KPI_INTEREST_ORDER = [...PROMO_INTEREST_KEYS, 'none'];
//# sourceMappingURL=promoKpi.js.map